import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Sparkles, X } from 'lucide-react';

/**
 * Lightweight modal triggered from in-app CTAs (e.g. AI 402, Free badge).
 * Does NOT inline checkout — sends users to /pricing where they pick a plan.
 * Keeps the modal focused on the "why upgrade" pitch.
 */
export default function UpgradeModal({ open, onClose, reason = 'AI Enhance' }) {
  const navigate = useNavigate();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="upgrade-modal-backdrop"
    >
      <div
        className="bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="upgrade-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-slate-500 hover:text-white"
          data-testid="upgrade-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-14 h-14 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg mb-4">
          <Crown className="w-7 h-7 text-white" />
        </div>

        <h2 className="text-2xl font-extrabold text-white text-center mb-1">
          {reason} is a Pro feature
        </h2>
        <p className="text-sm text-slate-400 text-center mb-5">
          Upgrade once and unlock everything below.
        </p>

        <ul className="space-y-2 mb-6 text-sm text-slate-200">
          <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> AI Finalize (hyper-realistic light bloom + shadows)</li>
          <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> No watermark on any export</li>
          <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Unlimited projects, HD export, Presentation Mode</li>
          <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> From <strong>$2.99/month</strong> — cancel any time</li>
        </ul>

        <div className="flex gap-2">
          <button
            onClick={() => { onClose?.(); navigate('/pricing'); }}
            data-testid="upgrade-modal-go-pricing"
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-lg shadow"
          >
            See plans
          </button>
          <button
            onClick={onClose}
            data-testid="upgrade-modal-later"
            className="px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm"
          >
            Maybe later
          </button>
        </div>

        <p className="text-[10px] text-slate-500 text-center mt-4">
          Or buy AI credit packs (one-time, never expire) on the pricing page.
        </p>
      </div>
    </div>
  );
}
